import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, Settings } from 'lucide-react';
import {
  StarIcon as FiAward,
  CommandLineIcon as FiFunction,
  CodeBracketIcon as FiCode,
} from '@heroicons/react/24/outline';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import CodesRubriquesPage from './CodesRubriquesPage';
import { DEFAULT_SALARIES_CONFIG, normalizeSalariesConfig } from './salariesConfigDefaults';
import { syncSalariesConfigToSettings, useSalairesConfig } from './useSalairesConfig';

const SalariesConfigPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.salariesConfigPage.${key}`, opts);
  const { patchSettings, settings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const liveConfig = useSalairesConfig();
  const [salariesConfig, setSalariesConfig] = useState(DEFAULT_SALARIES_CONFIG);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('config');

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (liveConfig && Object.keys(liveConfig).length > 0) {
      setSalariesConfig(liveConfig);
    }
  }, [liveConfig]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await localApiAdapter.get('/salaries/config');
      const raw = response?.data !== undefined ? response.data : response;
      const merged = normalizeSalariesConfig(raw || {});
      setSalariesConfig(merged);
      syncSalariesConfigToSettings(merged, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Error loading salaries config:', error);
      setSalariesConfig(normalizeSalariesConfig(liveConfig || {}));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await localApiAdapter.post('/salaries/config', salariesConfig);
      syncSalariesConfigToSettings(salariesConfig, patchSettings, settingsRef.current);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving salaries config:', error);
      alert(p('save_error') || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm(p('reset_confirm'))) {
      try {
        await localApiAdapter.post('/salaries/config/reset');
        setSalariesConfig(DEFAULT_SALARIES_CONFIG);
        syncSalariesConfigToSettings(DEFAULT_SALARIES_CONFIG, patchSettings, settingsRef.current);
      } catch (error) {
        console.error('Error resetting salaries config:', error);
        alert(p('reset_error') || t('common.error'));
      }
    }
  };

  const updateConfig = (key, value) => {
    setSalariesConfig((prev) => {
      const next = normalizeSalariesConfig({ ...prev, [key]: value });
      syncSalariesConfigToSettings(next, patchSettings, settingsRef.current);
      return next;
    });
  };

  const configContent = (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
      <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Calculator className="h-6 w-6" />
        {p('section_calculation')}
      </h2>
      <p className="text-white/60 mb-6">{p('section_calculation_desc')}</p>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('monthly_working_days')}</label>
          <input
            type="number"
            value={salariesConfig.monthlyWorkingDays}
            onChange={(e) => updateConfig('monthlyWorkingDays', Number(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('payment_day')}</label>
          <input
            type="number"
            value={salariesConfig.paymentDay}
            onChange={(e) => updateConfig('paymentDay', Number(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('overtime_rate')}</label>
          <input
            type="number"
            step="0.1"
            value={salariesConfig.overtimeRate}
            onChange={(e) => updateConfig('overtimeRate', Number(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('gosi_employee_rate')}</label>
          <input
            type="number"
            step="0.1"
            value={salariesConfig.gosiEmployeeRate}
            onChange={(e) => updateConfig('gosiEmployeeRate', Number(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('gosi_employer_rate')}</label>
          <input
            type="number"
            step="0.1"
            value={salariesConfig.gosiEmployerRate}
            onChange={(e) => updateConfig('gosiEmployerRate', Number(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('housing_allowance_pct')}</label>
          <input
            type="number"
            value={salariesConfig.housingAllowancePct}
            onChange={(e) => updateConfig('housingAllowancePct', Number(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('transport_allowance')}</label>
          <input
            type="number"
            value={salariesConfig.transportAllowance}
            onChange={(e) => updateConfig('transportAllowance', Number(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('mobile_allowance')}</label>
          <input
            type="number"
            value={salariesConfig.mobileAllowance}
            onChange={(e) => updateConfig('mobileAllowance', Number(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('food_allowance')}</label>
          <input
            type="number"
            value={salariesConfig.foodAllowance}
            onChange={(e) => updateConfig('foodAllowance', Number(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('gosi_max_basic')}</label>
          <input
            type="number"
            value={salariesConfig.gosiMaxBasic}
            onChange={(e) => updateConfig('gosiMaxBasic', Number(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('advance_prefix', { defaultValue: 'بادئة السلف' })}</label>
          <input
            type="text"
            value={salariesConfig.advanceReferencePrefix}
            onChange={(e) => updateConfig('advanceReferencePrefix', e.target.value)}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('loan_prefix', { defaultValue: 'بادئة القروض' })}</label>
          <input
            type="text"
            value={salariesConfig.loanReferencePrefix}
            onChange={(e) => updateConfig('loanReferencePrefix', e.target.value)}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('transfer_prefix', { defaultValue: 'بادئة التحويلات' })}</label>
          <input
            type="text"
            value={salariesConfig.transferReferencePrefix}
            onChange={(e) => updateConfig('transferReferencePrefix', e.target.value)}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('wps_enabled')}</label>
          <label className="flex items-center gap-3 text-white/80 mt-2">
            <input
              type="checkbox"
              checked={salariesConfig.wpsEnabled}
              onChange={(e) => updateConfig('wpsEnabled', e.target.checked)}
              className="w-5 h-5 rounded"
            />
            {p('wps_enabled_label')}
          </label>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        <label className="flex items-center gap-3 text-white/80">
          <input
            type="checkbox"
            checked={salariesConfig.autoCalculateDeductions}
            onChange={(e) => updateConfig('autoCalculateDeductions', e.target.checked)}
            className="w-5 h-5 rounded"
          />
          {p('auto_calculate_deductions')}
        </label>
        <label className="flex items-center gap-3 text-white/80">
          <input
            type="checkbox"
            checked={salariesConfig.autoGeneratePayslips}
            onChange={(e) => updateConfig('autoGeneratePayslips', e.target.checked)}
            className="w-5 h-5 rounded"
          />
          {p('auto_generate_payslips')}
        </label>
        <label className="flex items-center gap-3 text-white/80">
          <input
            type="checkbox"
            checked={salariesConfig.requireApproval}
            onChange={(e) => updateConfig('requireApproval', e.target.checked)}
            className="w-5 h-5 rounded"
          />
          {p('require_approval')}
        </label>
      </div>
    </div>
  );

  const primesContent = (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
      <h2 className="text-xl font-bold text-white mb-4">{p('section_bonuses_title')}</h2>
    </div>
  );

  const formulesContent = (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
      <h2 className="text-xl font-bold text-white mb-4">{p('section_formulas_title')}</h2>
      <p className="text-white/60">{p('section_formulas_desc')}</p>
      <div className="text-center py-12 text-white/40">
        <FiFunction className="h-16 w-16 mx-auto mb-4 opacity-50" />
        <p>{p('formulas_placeholder')}</p>
      </div>
    </div>
  );

  return (
    <ModuleConfigPageLayout
      title={p('title')}
      subtitle={p('subtitle')}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      tabs={[
        { id: 'config', label: p('tab_config'), icon: Settings, gradient: 'from-blue-500 to-blue-600' },
        { id: 'primes', label: p('tab_bonuses'), icon: FiAward, gradient: 'from-teal-500 to-teal-600' },
        { id: 'codes', label: p('tab_codes'), icon: FiCode, gradient: 'from-indigo-500 to-indigo-600' },
        { id: 'formules', label: p('tab_formulas'), icon: FiFunction, gradient: 'from-violet-500 to-violet-600' },
      ]}
      tabContent={{
        config: configContent,
        primes: primesContent,
        codes: <CodesRubriquesPage />,
        formules: formulesContent,
      }}
      saved={saved}
      savedMessage={p('saved_success')}
      loading={loading}
      saving={saving}
      onSave={handleSave}
      onReset={handleReset}
    />
  );
};

export default SalariesConfigPage;
